<template>
  <header
    id="header"
    class="header-nav"
    :class="{ 'navbar-fixed-top animated slideInDown': isSticky }"
    @animationstart="startHeaderOffsetSync"
    @animationend="stopHeaderOffsetSync"
    @animationcancel="stopHeaderOffsetSync"
  >
    <nav class="header-nav__inner" :aria-label="t('navigation.primary')">
      <router-link to="/" class="header-nav__brand" :aria-label="t('navigation.home')" @click="closeMobileMenu">
        <picture>
          <source media="(max-width: 700px)" srcset="/static/images/home-v2/h5/anovator-mark.svg">
          <img src="/static/images/home-v2/anovator-logo.svg" alt="ANOVATOR" width="127.2" height="19.2">
        </picture>
      </router-link>

      <button
        ref="mobileMenuToggle"
        class="mobile-menu-toggle"
        type="button"
        :class="{ active: isMobileMenuOpen }"
        :aria-expanded="isMobileMenuOpen"
        aria-controls="mainmenu"
        :aria-label="t('navigation.toggle')"
        @click="toggleMobileMenu"
      >
        <span />
        <span />
      </button>

      <div class="menu-wrap" :class="{ 'mobile-active': isMobileMenuOpen }" @click="handleMenuWrapClick">
        <div id="mainmenu" class="mainmenu header-nav-center" :lang="currentLanguage === 'ZH' ? 'zh-CN' : 'en'">
          <ul class="nav">
            <li
              class="nav-dropdown industries-menu"
              @focusout="handleIndustriesFocusOut"
            >
              <a
                href="#industries-menu"
                :class="{ active: isIndustriesMenuVisible || isIndustriesRoute }"
                aria-controls="industries-menu"
                :aria-expanded="isIndustriesMenuVisible"
                @mouseenter="showIndustriesMenu"
                @mouseleave="scheduleDropdownClose('industries')"
                @click.prevent="toggleIndustriesMenu"
              ><span class="header-nav__label">{{ t('navigation.industries') }}</span></a>
              <div
                id="industries-menu"
                class="industries-dropdown"
                :class="{ show: isIndustriesMenuVisible }"
                @click.self="hideIndustriesMenu"
              >
                <div class="industries-panel" @mouseenter="cancelDropdownClose" @mouseleave="scheduleDropdownClose('industries')">
                  <div class="industries-grid">
                    <section v-for="category in industryMenu" :key="category.key" class="industry-menu__category">
                      <router-link class="industry-menu__category-link" :to="buildIndustriesPath(category.key)" @click="closeMobileMenu">
                        {{ category.label }}
                      </router-link>
                      <ul>
                        <li v-for="item in category.items" :key="item.key">
                          <router-link v-if="isIndustryItemRoute(item)" :to="item.path" @click="closeMobileMenu">
                            {{ item.label }}
                          </router-link>
                          <span v-else>{{ item.label }}</span>
                        </li>
                      </ul>
                    </section>
                  </div>
                </div>
              </div>
            </li>
            <li class="nav-dropdown li-products">
              <a
                href="#products-menu"
                :class="{ active: isProductRoute }"
                aria-controls="products-menu"
                :aria-expanded="isProductsMenuVisible"
                @mouseenter="showProductsMenu"
                @mouseleave="scheduleDropdownClose('products')"
                @click.prevent="toggleProductsMenu"
              ><span class="header-nav__label">{{ t('navigation.products') }}</span></a>
              <div id="products-menu" class="products-list" :class="{ show: isProductsMenuVisible }" @mouseenter="cancelDropdownClose" @mouseleave="scheduleDropdownClose('products')">
                <button v-for="product in products" :key="product.path" class="product-nav-item" type="button" @click="navigateToProduct(product.path)">
                  <span class="product-nav-image">
                    <img class="product-photo" :src="product.image" :alt="product.name">
                  </span>
                  <span>{{ product.name }}</span>
                </button>
              </div>
            </li>
            <li class="nav-dropdown services-menu">
              <a
                href="#services-menu"
                aria-controls="services-menu"
                :aria-expanded="isServicesMenuVisible"
                @mouseenter="showServicesMenu"
                @mouseleave="scheduleDropdownClose('services')"
                @click.prevent="toggleServicesMenu"
              ><span class="header-nav__label">{{ t('navigation.services') }}</span></a>
              <ul id="services-menu" class="services-menu__list text-dropdown-list" :class="{ show: isServicesMenuVisible }" @mouseenter="cancelDropdownClose" @mouseleave="scheduleDropdownClose('services')">
                <li><a :href="navigationTargets.administratorPlatform" target="_blank" rel="noopener" @click="closeMobileMenu">{{ t('navigation.administratorPlatform') }}</a></li>
                <li><a :href="navigationTargets.apiDocumentation" target="_blank" rel="noopener" @click="closeMobileMenu">{{ t('navigation.apiDocumentation') }}</a></li>
              </ul>
            </li>
            <li
              class="nav-dropdown support-menu"
              @focusout="handleSupportFocusOut"
            >
              <router-link
                :to="navigationTargets.support"
                :class="{ active: isSupportRoute }"
                aria-controls="support-menu"
                :aria-expanded="isSupportMenuVisible"
                @mouseenter="showSupportMenu"
                @mouseleave="scheduleDropdownClose('support')"
                @click="closeMobileMenu"
              ><span class="header-nav__label">{{ t('navigation.support') }}</span></router-link>
              <ul id="support-menu" class="support-menu__list text-dropdown-list" :class="{ show: isSupportMenuVisible }" @mouseenter="cancelDropdownClose" @mouseleave="scheduleDropdownClose('support')">
                <li><router-link :to="navigationTargets.faq" @click="closeMobileMenu">{{ t('support.faq') }}</router-link></li>
              </ul>
            </li>
            <li @mouseenter="closeDropdowns"><router-link :to="navigationTargets.news" :class="{ active: isNewsRoute }" @click="closeMobileMenu"><span class="header-nav__label">{{ t('navigation.newsroom') }}</span></router-link></li>
            <li @mouseenter="closeDropdowns"><router-link :to="navigationTargets.about" :class="{ active: isAboutRoute }" @click="closeMobileMenu"><span class="header-nav__label">{{ t('navigation.about') }}</span></router-link></li>
            <li class="mobile-contact-item">
              <button type="button" @click="openContactModal">{{ t('navigation.contact') }}</button>
            </li>
          </ul>
        </div>
      </div>

      <div ref="siteSwitchActions" class="header-nav__actions">
        <button
          ref="contactTrigger"
          class="header-nav__action header-nav__contact"
          type="button"
          :aria-label="t('navigation.contact')"
          @click="openContactModal"
        >
          <img src="/static/images/home-v2/contact-headset-red.svg" alt="" width="28" height="28" aria-hidden="true">
        </button>
        <button
          ref="siteSwitchTrigger"
          class="header-nav__action header-nav__site-switch"
          type="button"
          :aria-label="t('navigation.switchSite')"
          aria-controls="site-switch-menu"
          :aria-expanded="isSiteSwitchMenuOpen"
          @click.stop="toggleSiteSwitchMenu"
        >
          <img src="/static/images/home-v2/site-switch-globe.svg" alt="" width="28" height="28" aria-hidden="true">
        </button>
        <div v-if="isSiteSwitchMenuOpen" id="site-switch-menu" class="site-switch-menu" role="menu" :aria-label="t('navigation.siteSwitcher')" @click.stop>
          <button class="site-switch-menu__option" :class="{ active: currentLanguage === 'EN' }" type="button" role="menuitem" :disabled="currentLanguage === 'EN'" @click="handleSiteSwitch('EN')">EN</button>
          <button class="site-switch-menu__option" :class="{ active: currentLanguage === 'ZH' }" type="button" role="menuitem" :disabled="currentLanguage === 'ZH'" @click="handleSiteSwitch('ZH')">ZH</button>
        </div>
      </div>
    </nav>

    <div
      v-if="isMobileMenuOpen"
      id="mobile-navigation"
      class="mobile-nav-overlay"
      role="dialog"
      aria-modal="true"
      :aria-label="t('navigation.mobile')"
    >
      <div class="mobile-nav-overlay__header">
        <button
          v-if="mobileMenuView !== 'main'"
          ref="mobileMenuBack"
          class="mobile-nav-overlay__icon"
          type="button"
          :aria-label="t('navigation.back')"
          @click="backToMobileMain"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14.5 5.5 8 12l6.5 6.5" /></svg>
        </button>
        <img
          v-else
          class="mobile-nav-overlay__mark"
          src="/static/images/home-v2/h5/anovator-mark.svg"
          alt="ANOVATOR"
          width="24"
          height="22"
        >
        <button
          class="mobile-nav-overlay__icon"
          type="button"
          :aria-label="t('navigation.close')"
          @click="closeMobileMenu"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
        </button>
      </div>

      <nav class="mobile-nav-overlay__content" :aria-label="t('navigation.mobile')">
        <ul v-if="mobileMenuView === 'main'" class="mobile-nav-overlay__main-list">
          <li><button :class="{ active: isIndustriesRoute }" type="button" @click="openMobileSubmenu('industries')">{{ t('navigation.industries') }}</button></li>
          <li><button :class="{ active: isProductRoute }" type="button" @click="openMobileSubmenu('products')">{{ t('navigation.products') }}</button></li>
          <li><button type="button" @click="openMobileSubmenu('services')">{{ t('navigation.services') }}</button></li>
          <li class="mobile-nav-overlay__main-item--submenu">
            <router-link :class="{ active: isSupportRoute }" :to="navigationTargets.support" @click="closeMobileMenu">{{ t('navigation.support') }}</router-link>
            <button class="mobile-nav-overlay__submenu-toggle" type="button" :aria-label="t('support.faq')" @click="openMobileSubmenu('support')">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg>
            </button>
          </li>
          <li><router-link :class="{ active: isNewsRoute }" :to="navigationTargets.news" @click="closeMobileMenu">{{ t('navigation.newsroom') }}</router-link></li>
          <li><router-link :class="{ active: isAboutRoute }" :to="navigationTargets.about" @click="closeMobileMenu">{{ t('navigation.about') }}</router-link></li>
        </ul>

        <ul v-else-if="mobileMenuView === 'products'" class="mobile-nav-overlay__sub-list mobile-nav-overlay__product-list">
          <li v-for="product in products" :key="product.path">
            <button type="button" :class="{ active: $route.path === product.path }" @click="navigateMobile(product.path)">
              <span class="mobile-nav-overlay__product-image"><img :src="product.image" :alt="product.name"></span>
              <span>{{ product.name }}</span>
            </button>
          </li>
        </ul>

        <ul v-else-if="mobileMenuView === 'services'" class="mobile-nav-overlay__sub-list">
          <li><a :href="navigationTargets.administratorPlatform" target="_blank" rel="noopener" @click="closeMobileMenu">{{ t('navigation.administratorPlatform') }}</a></li>
          <li><a :href="navigationTargets.apiDocumentation" target="_blank" rel="noopener" @click="closeMobileMenu">{{ t('navigation.apiDocumentation') }}</a></li>
        </ul>

        <ul v-else-if="mobileMenuView === 'support'" class="mobile-nav-overlay__sub-list">
          <li><router-link :to="navigationTargets.faq" @click="closeMobileMenu">{{ t('support.faq') }}</router-link></li>
        </ul>

        <div v-else class="mobile-nav-overlay__industries">
          <section v-for="category in industryMenu" :key="category.key" class="mobile-nav-overlay__industry-category">
            <router-link :to="`/industries/${category.key}`" @click="closeMobileMenu">{{ category.label }}</router-link>
            <ul>
              <li v-for="item in category.items" :key="item.key">
                <router-link :to="item.path" :class="{ active: $route.path === item.path }" @click="closeMobileMenu">{{ item.label }}</router-link>
              </li>
            </ul>
          </section>
        </div>
      </nav>
    </div>

    <ContactModal :open="isContactModalOpen" @close="closeContactModal" />
  </header>
</template>

<script>
import ContactModal from './ContactModal.vue'
import { solutionAPI } from '@/utils/api'

const { setLanguage, siteLanguageMixin } = require('@/utils/site-language.js')
const { createLatestSiteRequestGuard } = require('@/utils/site-request.cjs')
const { CONTACT_MODAL_OPEN_EVENT } = require('@/utils/contact-modal.cjs')
const { getProducts, navigationTargets, createIndustryMenu } = require('@/data/site-navigation.cjs')

const {
  getIndustryMenuItemType
} = require('@/utils/header-navigation.cjs')
const {
  buildIndustriesPath,
  normalizeIndustryCategoriesResponse,
  normalizeIndustryTopicsResponse
} = require('@/utils/industries-data.cjs')

export default {
  name: 'HeaderNav',
  components: { ContactModal },
  mixins: [siteLanguageMixin],
  data() {
    return {
      isSticky: false,
      isMobileMenuOpen: false,
      mobileMenuView: 'main',
      mobileBodyOverflow: null,
      mobileHtmlOverflow: null,
      isIndustriesMenuVisible: false,
      isProductsMenuVisible: false,
      isServicesMenuVisible: false,
      isSupportMenuVisible: false,
      desktopDropdownCloseTimer: null,
      isSiteSwitchMenuOpen: false,
      isContactModalOpen: false,
      contactReturnTarget: null,
      headerOffsetFrame: null,
      industryMenuRequestGuard: createLatestSiteRequestGuard(),
      industryMenu: []
    }
  },
  computed: {
    navigationTargets() { return navigationTargets },
    isProductRoute() {
      return ['/p5', '/products/p5', '/a5', '/m3', '/m1', '/m0', '/k2'].includes(this.$route.path)
    },
    isIndustriesRoute() {
      return this.$route.path.startsWith('/industries/')
    },
    isSupportRoute() {
      return this.$route.path === '/faq' || this.$route.path.startsWith('/support/')
    },
    isNewsRoute() {
      return this.$route.path === '/news' || this.$route.path.startsWith('/news/')
    },
    isAboutRoute() {
      return this.$route.path === '/about'
    },
    products() {
      return getProducts(this.currentLanguage)
    }
  },
  watch: {
    '$route.fullPath'() {
      this.closeMobileMenu()
    },
    currentLanguage() {
      this.loadIndustryMenu()
    }
  },
  mounted() {
    this.initStickyHeader()
    this.initHeaderHeight()
    this.loadIndustryMenu()
    window.addEventListener('resize', this.handleViewportResize)
    window.addEventListener('keydown', this.handleMobileKeydown)
    window.addEventListener(CONTACT_MODAL_OPEN_EVENT, this.handleContactModalRequest)
    document.addEventListener('click', this.handleSiteSwitchOutside)
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('resize', this.handleViewportResize)
    window.removeEventListener('keydown', this.handleMobileKeydown)
    window.removeEventListener(CONTACT_MODAL_OPEN_EVENT, this.handleContactModalRequest)
    document.removeEventListener('click', this.handleSiteSwitchOutside)
    window.cancelAnimationFrame(this.headerOffsetFrame)
    this.cancelDropdownClose()
    this.unlockMobileScroll()
    document.documentElement.style.removeProperty('--global-header-height')
    document.documentElement.style.removeProperty('--global-header-offset')
  },
  methods: {
    async loadIndustryMenu() {
      const request = this.industryMenuRequestGuard.start(this.currentLanguage)
      try {
        const categories = normalizeIndustryCategoriesResponse(
          await solutionAPI.getCategories(request.siteType)
        )
        if (!this.industryMenuRequestGuard.isCurrent(request, this.currentLanguage)) return
        const menus = await Promise.all(categories.map(async (category) => {
          const topics = normalizeIndustryTopicsResponse(
            await solutionAPI.getTopics(category.categoryId, request.siteType)
          )
          return createIndustryMenu(category, topics)
        }))
        if (!this.industryMenuRequestGuard.isCurrent(request, this.currentLanguage)) return
        this.industryMenu = menus
      } catch (error) {
        if (!this.industryMenuRequestGuard.isCurrent(request, this.currentLanguage)) return
        this.industryMenu = []
        console.error('Failed to load Industries navigation:', error)
      }
    },
    isIndustryItemRoute(item) {
      return getIndustryMenuItemType(item) === 'route'
    },
    buildIndustriesPath(categoryId) {
      return buildIndustriesPath(categoryId)
    },
    navigateToProduct(path) {
      this.$router.push(path)
      this.closeMobileMenu()
    },
    toggleMobileMenu() {
      if (this.isMobileMenuOpen) {
        this.closeMobileMenu()
        return
      }
      this.mobileMenuView = 'main'
      this.isMobileMenuOpen = true
      this.closeDropdowns()
      this.lockMobileScroll()
    },
    closeMobileMenu() {
      this.isMobileMenuOpen = false
      this.mobileMenuView = 'main'
      this.closeDropdowns()
      this.unlockMobileScroll()
    },
    openMobileSubmenu(view) {
      this.mobileMenuView = view
      this.$nextTick(() => {
        const backButton = this.$refs.mobileMenuBack
        if (backButton && typeof backButton.focus === 'function') backButton.focus()
      })
    },
    backToMobileMain() {
      this.mobileMenuView = 'main'
    },
    navigateMobile(path) {
      this.closeMobileMenu()
      this.$router.push(path)
    },
    handleMobileKeydown(event) {
      if (event.key === 'Escape' && this.isSiteSwitchMenuOpen) {
        this.closeSiteSwitchMenu()
        return
      }
      if (event.key === 'Escape' && this.isMobileMenuOpen) this.closeMobileMenu()
    },
    handleViewportResize() {
      this.initHeaderHeight()
      if (window.matchMedia('(max-width: 700px)').matches) this.closeSiteSwitchMenu()
      if (!window.matchMedia('(max-width: 700px)').matches) this.closeMobileMenu()
    },
    lockMobileScroll() {
      if (!window.matchMedia('(max-width: 700px)').matches || this.mobileBodyOverflow !== null) return
      this.mobileBodyOverflow = document.body.style.overflow
      this.mobileHtmlOverflow = document.documentElement.style.overflow
      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
    },
    unlockMobileScroll() {
      if (this.mobileBodyOverflow === null) return
      document.body.style.overflow = this.mobileBodyOverflow
      document.documentElement.style.overflow = this.mobileHtmlOverflow
      this.mobileBodyOverflow = null
      this.mobileHtmlOverflow = null
    },
    closeDropdowns() {
      this.cancelDropdownClose()
      this.isIndustriesMenuVisible = false
      this.isProductsMenuVisible = false
      this.isServicesMenuVisible = false
      this.isSupportMenuVisible = false
    },
    cancelDropdownClose() {
      if (this.desktopDropdownCloseTimer !== null) window.clearTimeout(this.desktopDropdownCloseTimer)
      this.desktopDropdownCloseTimer = null
    },
    openDesktopDropdown(menu) {
      if (window.matchMedia('(max-width: 700px)').matches) return
      this.cancelDropdownClose()
      this.isIndustriesMenuVisible = menu === 'industries'
      this.isProductsMenuVisible = menu === 'products'
      this.isServicesMenuVisible = menu === 'services'
      this.isSupportMenuVisible = menu === 'support'
    },
    scheduleDropdownClose(menu) {
      if (window.matchMedia('(max-width: 700px)').matches) return
      this.cancelDropdownClose()
      this.desktopDropdownCloseTimer = window.setTimeout(() => {
        if (menu === 'industries') this.isIndustriesMenuVisible = false
        if (menu === 'products') this.isProductsMenuVisible = false
        if (menu === 'services') this.isServicesMenuVisible = false
        if (menu === 'support') this.isSupportMenuVisible = false
        this.desktopDropdownCloseTimer = null
      }, 80)
    },
    toggleSiteSwitchMenu() {
      const nextOpen = !this.isSiteSwitchMenuOpen
      this.closeDropdowns()
      this.isSiteSwitchMenuOpen = nextOpen
    },
    closeSiteSwitchMenu() {
      this.isSiteSwitchMenuOpen = false
    },
    handleSiteSwitchOutside(event) {
      if (this.isSiteSwitchMenuOpen && !this.$refs.siteSwitchActions?.contains(event.target)) this.closeSiteSwitchMenu()
    },
    handleSiteSwitch(siteType) {
      setLanguage(siteType)
      this.closeSiteSwitchMenu()
    },
    handleMenuWrapClick(event) {
      if (event.target.classList.contains('menu-wrap')) this.closeMobileMenu()
    },
    showIndustriesMenu() {
      this.openDesktopDropdown('industries')
    },
    hideIndustriesMenu() {
      this.scheduleDropdownClose('industries')
    },
    handleIndustriesFocusOut(event) {
      if (window.matchMedia('(max-width: 700px)').matches) return
      if (!event.currentTarget.contains(event.relatedTarget)) this.isIndustriesMenuVisible = false
    },
    toggleIndustriesMenu() {
      this.cancelDropdownClose()
      this.isIndustriesMenuVisible = !this.isIndustriesMenuVisible
      this.isProductsMenuVisible = false
      this.isServicesMenuVisible = false
      this.isSupportMenuVisible = false
    },
    showProductsMenu() {
      this.openDesktopDropdown('products')
    },
    hideProductsMenu() {
      this.scheduleDropdownClose('products')
    },
    toggleProductsMenu() {
      this.cancelDropdownClose()
      this.isProductsMenuVisible = !this.isProductsMenuVisible
      this.isIndustriesMenuVisible = false
      this.isServicesMenuVisible = false
      this.isSupportMenuVisible = false
    },
    showServicesMenu() {
      this.openDesktopDropdown('services')
    },
    hideServicesMenu() {
      this.scheduleDropdownClose('services')
    },
    toggleServicesMenu() {
      this.cancelDropdownClose()
      this.isServicesMenuVisible = !this.isServicesMenuVisible
      this.isIndustriesMenuVisible = false
      this.isProductsMenuVisible = false
      this.isSupportMenuVisible = false
    },
    showSupportMenu() {
      this.openDesktopDropdown('support')
    },
    hideSupportMenu() {
      this.scheduleDropdownClose('support')
    },
    handleSupportFocusOut(event) {
      if (window.matchMedia('(max-width: 700px)').matches) return
      if (!event.currentTarget.contains(event.relatedTarget)) this.isSupportMenuVisible = false
    },
    toggleSupportMenu() {
      this.cancelDropdownClose()
      this.isSupportMenuVisible = !this.isSupportMenuVisible
      this.isIndustriesMenuVisible = false
      this.isProductsMenuVisible = false
      this.isServicesMenuVisible = false
    },
    handleContactModalRequest(event) {
      this.openContactModal({ currentTarget: event?.detail?.trigger || null })
    },
    openContactModal(event) {
      const isMobile = window.matchMedia('(max-width: 700px)').matches
      this.contactReturnTarget = isMobile ? this.$refs.mobileMenuToggle : (event?.currentTarget || this.$refs.contactTrigger)
      this.closeMobileMenu()
      this.isContactModalOpen = true
    },
    closeContactModal() {
      this.isContactModalOpen = false
      this.$nextTick(() => {
        const target = this.contactReturnTarget || this.$refs.contactTrigger
        if (target && typeof target.focus === 'function') target.focus()
        this.contactReturnTarget = null
      })
    },
    initStickyHeader() {
      window.addEventListener('scroll', this.handleScroll)
    },
    handleScroll() {
      const header = document.getElementById('header')
      const nextSticky = window.scrollY >= (header ? header.offsetHeight : 64)
      if (nextSticky !== this.isSticky) {
        this.isSticky = nextSticky
        this.$nextTick(this.updateGlobalHeaderOffset)
      }
    },
    initHeaderHeight() {
      this.$nextTick(() => {
        const header = document.getElementById('header')
        if (!header) return
        const height = header.offsetHeight
        document.documentElement.style.setProperty('--global-header-height', `${height}px`)
        document.querySelectorAll('.header-height').forEach((element) => {
          element.style.height = `${height}px`
        })
        this.updateGlobalHeaderOffset()
      })
    },
    updateGlobalHeaderOffset() {
      const header = document.getElementById('header')
      if (!header) return
      const height = header.offsetHeight
      const offset = Math.max(0, Math.min(height, header.getBoundingClientRect().bottom))
      document.documentElement.style.setProperty('--global-header-height', `${height}px`)
      document.documentElement.style.setProperty('--global-header-offset', `${offset}px`)
    },
    startHeaderOffsetSync(event) {
      if (event.target !== event.currentTarget) return
      window.cancelAnimationFrame(this.headerOffsetFrame)
      const syncOffset = () => {
        this.updateGlobalHeaderOffset()
        this.headerOffsetFrame = window.requestAnimationFrame(syncOffset)
      }
      syncOffset()
    },
    stopHeaderOffsetSync(event) {
      if (event.target !== event.currentTarget) return
      window.cancelAnimationFrame(this.headerOffsetFrame)
      this.headerOffsetFrame = null
      this.updateGlobalHeaderOffset()
    }
  }
}
</script>

<style scoped>
.header-nav,
.header-nav * {
  box-sizing: border-box;
}

.header-nav {
  --header-nav-height: 64px;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1000;
  width: 100%;
  height: var(--header-nav-height);
  padding: 0;
  background: #fff;
  transition: box-shadow 0.25s ease;
}

.header-nav.navbar-fixed-top {
  position: fixed;
  height: var(--header-nav-height);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}

.header-nav__inner {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 1440px;
  height: var(--header-nav-height);
  min-height: var(--header-nav-height);
  margin: 0 auto;
  padding: 0 72px;
}

.header-nav__brand {
  display: flex;
  flex: 0 0 127.2px;
  align-items: center;
  height: var(--header-nav-height);
  padding: 0;
}

.header-nav__brand img {
  display: block;
  width: 127.2px;
  height: 19.2px;
  max-width: none;
}

.menu-wrap {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  height: var(--header-nav-height);
}

.mainmenu,
.nav {
  height: 100%;
}

.nav {
  display: flex;
  align-items: center;
  gap: clamp(44px, 4.4vw, 70px);
  margin: 0;
  padding: 0;
  list-style: none;
}

.nav > li {
  position: relative;
  display: flex;
  align-items: center;
  height: var(--header-nav-height);
  margin: 0;
  padding: 0;
}

.nav a {
  color: #3f3f41;
  font-family: 'MiSans-Regular', 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: var(--header-nav-height);
  text-decoration: none;
  white-space: nowrap;
  transition: color 0.2s ease;
}

.nav a:hover,
.nav a:focus-visible,
.nav a.active {
  color: #ef0a6a !important;
}

.header-nav__actions {
  position: relative;
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 20px;
}

.header-nav__action {
  display: flex;
  flex: 0 0 28px;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.header-nav__action:focus {
  outline: none;
}

.header-nav__action:focus-visible {
  outline: 2px solid #ef0a6a;
  outline-offset: 3px;
}

.header-nav__action img {
  display: block;
  width: 28px;
  height: 28px;
  max-width: none;
  opacity: 1;
  transition: opacity 0.2s ease;
}

.header-nav__action:hover img,
.header-nav__action:focus-visible img {
  opacity: 0.6;
}

.site-switch-menu {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  z-index: 4;
  display: grid;
  min-width: 72px;
  padding: 6px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.site-switch-menu__option {
  width: 100%;
  min-height: 32px;
  padding: 0 12px;
  color: #111;
  background: transparent;
  border: 0;
  border-radius: 4px;
  font: 400 12px/32px 'MiSans-Regular', 'Segoe UI', sans-serif;
  text-align: left;
  cursor: pointer;
}

.site-switch-menu__option.active {
  color: #ef0a6a;
  cursor: default;
}

.site-switch-menu__option:focus-visible {
  outline: 2px solid #ef0a6a;
  outline-offset: 1px;
}

.site-switch-menu__option:not(:disabled):hover,
.site-switch-menu__option:not(:disabled):focus-visible {
  color: #ef0a6a;
}

.industries-dropdown {
  position: fixed;
  top: var(--header-nav-height);
  left: 50%;
  z-index: 2;
  width: min(100%, 1440px);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transform: translateX(-50%) translateY(-8px);
  transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease;
}

.industries-dropdown.show {
  opacity: 1;
  visibility: visible;
  pointer-events: none;
  transform: translateX(-50%) translateY(0);
}

.industries-panel {
  min-height: 269px;
  padding-bottom: 24px;
  background: #fff;
  pointer-events: none;
}

.industries-dropdown.show .industries-panel {
  pointer-events: auto;
}

.industries-grid {
  display: grid;
  width: fit-content;
  max-width: calc(100% - 144px);
  margin: 0 auto;
  padding-top: 30px;
  grid-template-columns: repeat(3, max-content);
  column-gap: 48px;
}

@media (min-width: 1024px) {
  .industry-menu__category {
    min-width: 160px;
    max-width: 320px;
  }

  #mainmenu .industry-menu__category a,
  .industry-menu__category span {
    white-space: normal;
    overflow-wrap: break-word;
  }
}

.industry-menu__category h2 {
  margin: 0 0 16px;
  color: rgba(0, 0, 0, 0.6);
  font-family: 'MiSans-Regular', 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  white-space: nowrap;
}

#mainmenu .industry-menu__category ul {
  position: static;
  z-index: auto;
  display: block;
  width: auto;
  margin: 0;
  padding: 0;
  background: transparent;
  box-shadow: none;
  list-style: none;
  opacity: 1;
  visibility: visible;
  transform: none;
}

#mainmenu .industry-menu__category li {
  position: static;
  width: auto;
  height: auto;
  margin: 0;
  padding: 0;
  border: 0;
}

#mainmenu .industry-menu__category a,
.industry-menu__category span {
  display: block;
  color: #000;
  font-family: 'MiSans-Regular', 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 25px;
  white-space: normal;
  overflow-wrap: break-word;
  transition: color 0.2s ease;
}

#mainmenu .industry-menu__category a:hover,
#mainmenu .industry-menu__category a:focus-visible,
.industry-menu__category span:hover {
  color: #ef0a6a;
}

#mainmenu .industry-menu__category .industry-menu__category-link {
  margin: 0 0 16px;
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
  line-height: 24px;
  text-decoration: none;
}

.products-list {
  position: fixed;
  top: var(--header-nav-height);
  left: 50%;
  display: flex;
  width: min(100%, 1440px);
  padding: 22px 72px 26px;
  background: #fff;
  box-shadow: 0 18px 32px rgba(0, 0, 0, 0.08);
  opacity: 0;
  visibility: hidden;
  transform: translateX(-50%) translateY(-8px);
  transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease;
}

.products-list.show {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(0);
}

.li-products:hover > .products-list,
.li-products .products-list.show {
  top: var(--header-nav-height);
}

.product-nav-item {
  display: flex;
  flex: 1 1 20%;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  height: 220px;
  padding: 0 12px 12px;
  color: #3f3f41;
  background: transparent;
  border: 0;
  font-family: 'MiSans-Regular', sans-serif;
  font-size: 14px;
  cursor: pointer;
}

.product-nav-item:hover,
.product-nav-item:focus-visible {
  color: #ef0a6a;
  background: #f5f5f7;
}

@media (min-width: 701px) {
  .product-nav-item:focus {
    outline: none;
  }

  .product-nav-item:focus-visible {
    outline: 2px solid #ef0a6a;
    outline-offset: 3px;
  }
}

.product-nav-image {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 178px;
  overflow: hidden;
}

.product-photo {
  display: block;
  width: 100%;
  height: 100%;
  max-width: 100%;
  object-fit: contain;
}

.text-dropdown-list {
  position: absolute;
  top: var(--header-nav-height);
  left: 50%;
  z-index: 2;
  width: 220px;
  margin: 0;
  padding: 8px 0;
  background: #fff;
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.1);
  list-style: none;
  opacity: 0;
  visibility: hidden;
  transform: translateX(-50%) translateY(-8px);
  transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease;
}

.text-dropdown-list.show {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(0);
}

@media (min-width: 701px) {
  #mainmenu .text-dropdown-list {
    position: absolute;
    top: var(--header-nav-height);
    left: 50%;
    z-index: 2;
    width: 220px;
    height: auto;
    margin: 0;
    padding: 8px 0;
    background: #fff;
    box-shadow: 0 14px 30px rgba(0, 0, 0, 0.1);
    list-style: none;
    opacity: 0;
    visibility: hidden;
    transform: translateX(-50%) translateY(-8px);
    transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease;
  }

  #mainmenu .text-dropdown-list.show {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
  }
}

#mainmenu .text-dropdown-list a {
  display: block;
  padding: 0 18px;
  color: #3f3f41;
  background: transparent;
  font-family: 'MiSans-Regular', 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: normal;
  line-height: 38px;
}

#mainmenu .text-dropdown-list > li:hover > a,
#mainmenu .text-dropdown-list > li > a:focus-visible {
  color: #ef0a6a;
  background: #f5f5f7;
}

.mobile-menu-toggle {
  display: none;
}

.mobile-nav-overlay {
  display: none;
}

.mobile-contact-item {
  display: none !important;
}

@media (min-width: 701px) {
  .header-nav-center {
    position: absolute;
    right: 0;
    left: 0;
    width: max-content;
    margin: 0 auto;
    transform: none;
  }

  .header-nav-center .nav {
    flex-wrap: nowrap;
    width: max-content;
  }
}

@media (min-width: 1024px) {
  .header-nav-center .nav { gap: 0; }
  .header-nav-center .nav > li {
    flex: 0 0 auto;
    min-width: 80px;
  }

  .header-nav-center .nav > li > a {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: center;
    padding: 0 clamp(18px, 1.75vw, 24px) !important;
    text-align: center;
  }

  .header-nav-center:lang(zh) .nav > li > a {
    padding: 0 28px !important;
  }

  .header-nav__label {
    position: relative;
    display: inline-block;
  }

  .header-nav__label::after {
    content: '';
    position: absolute;
    width: 40px;
    height: 2px;
    bottom: 0;
    background: transparent;
    left: 50%;
    transform: translateX(-50%);
  }

  .header-nav-center .nav > li > a.active .header-nav__label::after {
    background: #ef0a6a;
  }
}

@media (max-width: 1023px) and (min-width: 701px) {
  .header-nav__inner {
    padding: 0 32px;
  }

  .nav {
    gap: 8px;
  }

  #mainmenu > .nav > li > a {
    padding: 0 !important;
  }

  .products-list {
    padding-right: 32px;
    padding-left: 32px;
  }

  .industries-grid {
    width: calc(100% - 64px);
    max-width: none;
    grid-template-columns: 1.4fr 0.9fr 1.2fr;
    column-gap: 24px;
  }

  .industry-menu__category h2,
  #mainmenu .industry-menu__category a,
  .industry-menu__category span {
    font-size: 14px;
  }
}

@media (min-width: 701px) and (max-width: 1199px) {
  .header-nav__inner {
    gap: 16px;
    padding: 0 24px;
  }

  .menu-wrap { min-width: 0; }

  .header-nav-center {
    position: static;
    width: auto;
    margin: 0;
  }

  .header-nav-center .nav { gap: 0; }
  .header-nav-center .nav > li { min-width: 0; }
  #mainmenu > .nav > li > a,
  #mainmenu:lang(zh) > .nav > li > a { padding: 0 8px !important; }
}

@media (min-width: 701px) and (max-width: 900px) {
  .header-nav__inner {
    gap: 8px;
    padding: 0 16px;
  }

  .header-nav__brand { flex-basis: clamp(106px, 16.563vw, 127.2px); }
  .header-nav__brand img { width: clamp(106px, 16.563vw, 127.2px); height: auto; }
  .header-nav-center .nav { gap: 8px; }
  #mainmenu > .nav > li > a,
  #mainmenu:lang(zh) > .nav > li > a {
    padding: 0 !important;
    font-size: clamp(12px, 1.693vw, 13px);
  }

  .header-nav__actions { gap: 8px; }
  .header-nav__action { flex-basis: clamp(24px, 3.646vw, 28px); }
  .header-nav__action,
  .header-nav__action img { width: clamp(24px, 3.646vw, 28px); height: clamp(24px, 3.646vw, 28px); }
}

@media (max-width: 700px) {
  .header-nav { --header-nav-height: 50px; }
  .header-nav,
  .header-nav.navbar-fixed-top,
  .header-nav__inner {
    height: 50px;
  }

  .header-nav__inner {
    min-height: 50px;
  }

  .header-nav__inner {
    justify-content: space-between;
    padding: 0 16px;
  }

  .header-nav__brand {
    flex: 0 0 44px;
    width: 44px;
    height: 50px;
  }

  .header-nav__brand picture,
  .header-nav__brand img {
    display: block;
    width: 28.8px;
    height: 26.4px;
  }

  .header-nav__actions {
    display: none;
  }

  .menu-wrap {
    display: none;
  }

  .mobile-nav-overlay {
    position: fixed;
    inset: 0;
    z-index: 1003;
    display: flex;
    flex-direction: column;
    width: 100vw;
    height: 100dvh;
    overflow: hidden;
    color: #111;
    background: #fff;
  }

  .mobile-nav-overlay__header {
    display: flex;
    flex: 0 0 50px;
    align-items: center;
    justify-content: space-between;
    height: 50px;
    padding: 0 16px;
  }

  .mobile-nav-overlay__mark {
    display: block;
    width: 24px;
    height: 22px;
  }

  .mobile-nav-overlay__icon {
    display: grid;
    width: 24px;
    height: 24px;
    padding: 0;
    place-items: center;
    color: #111;
    background: transparent;
    border: 0;
    cursor: pointer;
  }

  .mobile-nav-overlay__icon svg {
    display: block;
    width: 24px;
    height: 24px;
    fill: none;
    stroke: currentColor;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 1.5;
  }

  .mobile-nav-overlay__icon:focus-visible,
  .mobile-nav-overlay__main-list a:focus-visible,
  .mobile-nav-overlay__main-list button:focus-visible,
  .mobile-nav-overlay__sub-list a:focus-visible,
  .mobile-nav-overlay__sub-list button:focus-visible,
  .mobile-nav-overlay__industries a:focus-visible {
    outline: 2px solid #ef0a6a;
    outline-offset: 3px;
  }

  .mobile-nav-overlay__content {
    flex: 1 1 auto;
    min-height: 0;
    padding: 42px 16px 48px;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .mobile-nav-overlay__main-list,
  .mobile-nav-overlay__sub-list,
  .mobile-nav-overlay__industries,
  .mobile-nav-overlay__industries ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .mobile-nav-overlay__main-list {
    display: grid;
    gap: 28px;
  }

  .mobile-nav-overlay__main-list a,
  .mobile-nav-overlay__main-list button {
    display: block;
    width: 100%;
    padding: 0;
    color: #111;
    background: transparent;
    border: 0;
    font: 700 26px/1.23 'MiSans-Regular', 'Segoe UI', sans-serif;
    letter-spacing: 0;
    text-align: left;
    text-decoration: none;
    cursor: pointer;
  }

  .mobile-nav-overlay__main-list .active,
  .mobile-nav-overlay__sub-list .active,
  .mobile-nav-overlay__industries .active {
    color: #ef0a6a;
  }

  .mobile-nav-overlay__main-item--submenu {
    display: flex;
    align-items: center;
  }

  .mobile-nav-overlay__main-item--submenu > a {
    flex: 1;
  }

  .mobile-nav-overlay__main-list .mobile-nav-overlay__submenu-toggle {
    flex: 0 0 32px;
    width: 32px;
    height: 32px;
    margin-left: 12px;
  }

  .mobile-nav-overlay__submenu-toggle svg {
    width: 24px;
    height: 24px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.5;
  }

  .mobile-nav-overlay__sub-list {
    display: grid;
    gap: 24px;
  }

  .mobile-nav-overlay__sub-list a,
  .mobile-nav-overlay__sub-list button {
    display: block;
    width: 100%;
    padding: 0;
    color: #111;
    background: transparent;
    border: 0;
    font: 700 22px/1.3 'MiSans-Regular', 'Segoe UI', sans-serif;
    text-align: left;
    text-decoration: none;
    cursor: pointer;
  }

  .mobile-nav-overlay__product-list {
    gap: 16px;
  }

  .mobile-nav-overlay__product-list button {
    display: grid;
    grid-template-columns: 76px 1fr;
    gap: 16px;
    align-items: center;
  }

  .mobile-nav-overlay__product-image {
    display: grid;
    width: 76px;
    height: 76px;
    overflow: hidden;
    place-items: center;
    background: #f5f5f7;
  }

  .mobile-nav-overlay__product-image img {
    display: block;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  .mobile-nav-overlay__industries {
    display: grid;
    gap: 32px;
  }

  .mobile-nav-overlay__industry-category > a {
    display: block;
    color: #111;
    font: 700 22px/1.3 'MiSans-Regular', 'Segoe UI', sans-serif;
    text-decoration: none;
  }

  .mobile-nav-overlay__industry-category ul {
    display: grid;
    gap: 14px;
    margin-top: 16px;
  }

  .mobile-nav-overlay__industry-category li a {
    display: block;
    color: #3f3f41;
    font: 16px/1.45 'MiSans-Regular', 'Segoe UI', sans-serif;
    text-decoration: none;
  }

  .mobile-contact-item {
    display: block !important;
  }

  .mobile-contact-item button {
    display: block;
    width: 100%;
    height: 54px;
    padding: 0;
    color: #3f3f41;
    background: transparent;
    border: 0;
    font: 16px/54px 'MiSans-Regular', 'Segoe UI', sans-serif;
    text-align: left;
    cursor: pointer;
  }

  .mobile-contact-item button:hover,
  .mobile-contact-item button:focus-visible {
    color: #ef0a6a;
  }

  .mobile-menu-toggle {
    position: relative;
    z-index: 1002;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 44px;
    height: 44px;
    padding: 0;
    gap: 8px;
    background: transparent;
    border: 0;
    cursor: pointer;
  }

  .mobile-menu-toggle span {
    display: block;
    width: 18px;
    height: 1px;
    margin: 0 auto;
    background: #111;
    transform-origin: center;
    transition: transform 0.2s ease, opacity 0.2s ease;
  }

  .mobile-menu-toggle.active span:first-child {
    transform: translateY(4.5px) rotate(45deg);
  }

  .mobile-menu-toggle.active span:last-child {
    transform: translateY(-4.5px) rotate(-45deg);
  }

  .menu-wrap {
    position: fixed;
    top: 50px;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1001;
    display: block;
    height: calc(100vh - 50px);
    padding: 18px 16px 32px;
    overflow-y: auto;
    background: #fff;
    opacity: 0;
    visibility: hidden;
    transform: translateX(100%);
    transition: opacity 0.25s ease, transform 0.25s ease, visibility 0.25s ease;
  }

  .menu-wrap.mobile-active {
    opacity: 1;
    visibility: visible;
    transform: translateX(0);
  }

  .mainmenu,
  .nav {
    height: auto;
  }

  .nav {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0;
  }

  .nav > li {
    display: block;
    height: auto;
    border-bottom: 1px solid #ececef;
  }

  .nav > li > a {
    display: block;
    font-size: 16px;
    line-height: 54px;
  }

  .products-list,
  .text-dropdown-list,
  .industries-dropdown {
    position: static;
    display: none;
    width: 100%;
    height: auto;
    padding: 0 0 12px;
    background: #fff;
    box-shadow: none;
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transform: none;
    backdrop-filter: none;
  }

  .products-list.show,
  .text-dropdown-list.show,
  .industries-dropdown.show {
    display: block;
    transform: none;
  }

  .industries-menu > a::after,
  .header-nav__label::after {
    display: none;
  }

  .industries-panel {
    height: auto;
  }

  .industries-grid {
    display: block;
    width: 100%;
    max-width: none;
    padding: 0 0 4px;
  }

  .industry-menu__category {
    padding-bottom: 8px;
  }

  .industry-menu__category h2 {
    margin: 0;
    padding-left: 14px;
    font-size: 14px;
    line-height: 42px;
  }

  #mainmenu .industry-menu__category a,
  .industry-menu__category span {
    padding-left: 28px;
    font-size: 14px;
    line-height: 38px;
    white-space: normal;
  }

  .product-nav-item {
    display: grid;
    width: 100%;
    height: 74px;
    padding: 0;
    grid-template-columns: 74px 1fr;
    align-items: center;
    text-align: left;
  }

  .product-nav-image {
    height: 70px;
  }

  #mainmenu .text-dropdown-list a {
    padding-left: 14px;
    font-size: 14px;
    line-height: 42px;
  }

  .menu-wrap,
  .menu-wrap.mobile-active {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .header-nav,
  .products-list,
  .text-dropdown-list,
  .industries-dropdown,
  .menu-wrap,
  .mobile-menu-toggle span {
    transition: none;
  }
}
</style>
